import firebase from 'src/firebase/firebase'

export const getBookingData = async (id: string): Promise<any> => {
  return firebase.db
    .collection('bookings')
    .doc(id)
    .get()
    .then((response) => {
      const data = response.data()
      return data
    })
    .catch(() => {
      throw new Error('Hubo un error mientras se intentaba recuperar los datos de la cita')
    })
}

export const getAllBookings = async (): Promise<any> => {
  try {
    return await firebase.db
      .collection('bookings')
      .get()
      .then((snapshot: any) => {
        if (snapshot.empty) {
          return []
        }
        return snapshot.docs.map((doc: any) => {
          return { id: doc.id, ...doc.data() }
        })
      })
  } catch (error) {
    throw new Error('Hubo un error mientras se intentaban recuperar las citas')
  }
}

export const getAllBookingsByCompany = async (companyId: string): Promise<any> => {
  try {
    return await firebase.db
      .collection('bookings')
      .where('companyId', '==', companyId)
      .get()
      .then((snapshot: any) => {
        if (snapshot.empty) {
          return []
        }
        return snapshot.docs.map((doc: any) => {
          return { id: doc.id, ...doc.data() }
        })
      })
  } catch (error) {
    throw new Error('Hubo un error mientras se intentaban recuperar las citas')
  }
}

export const getAllBookingsByCompanyAndStore = async (companyId: string, storeId: string): Promise<any> => {
  try {
    return await firebase.db
      .collection('bookings')
      .where('companyId', '==', companyId)
      .where('storeId', '==', storeId)
      .get()
      .then((snapshot: any) => {
        if (snapshot.empty) {
          return []
        }
        return snapshot.docs.map((doc: any) => {
          return { id: doc.id, ...doc.data() }
        })
      })
  } catch (error) {
    throw new Error('Hubo un error mientras se intentaban recuperar las citas')
  }
}

export const getAllBookingsForPhysio = async (storesArr: []): Promise<any> => {
  try {
    return await firebase.db
      .collection('bookings')
      .where('storeId', 'in', storesArr)
      .get()
      .then((snapshot: any) => {
        if (snapshot.empty) {
          return []
        }
        return snapshot.docs.map((doc: any) => {
          return { id: doc.id, ...doc.data() }
        })
      })
  } catch (error) {
    throw new Error('Hubo un error mientras se intentaban recuperar las citas del fisioterapeuta')
  }
}

export const getUserBookings = async (userId: string): Promise<any> => {
  try {
    return await firebase.db
      .collection('bookings')
      .where('patient.id', '==', userId)
      .get()
      .then((snapshot: any) => {
        if (snapshot.empty) {
          return []
        }
        return snapshot.docs.map((doc: any) => {
          return { id: doc.id, ...doc.data() }
        })
      })
  } catch (error) {
    throw new Error('Hubo un error mientras se intentaban recuperar las citas del empleado')
  }
}

export const createBooking = async (bookingData: Record<string, any>): Promise<any> => {
  // La function crea la cita y reserva la franja de forma atómica: no hay que tocar el
  // calendario desde cliente.
  const createBookingCloud = firebase.functions.httpsCallable('createBookingCloud')

  try {
    return await createBookingCloud(bookingData)
  } catch (error: any) {
    console.log(error)
    // La franja se ocupó entre que el usuario la vio y confirmó: lo propagamos
    // para que la UI avise y refresque, en vez del error genérico.
    const msg = error?.message || ''
    if (msg.includes('SLOT_TAKEN')) throw new Error('SLOT_TAKEN')
    if (msg.includes('SLOT_NOT_FOUND')) throw new Error('SLOT_NOT_FOUND')
    throw new Error('Hubo un error mientras se intentaba crear la cita')
  }
}

export const sendNewBookingNotification = async (bookingData: Record<string, any>): Promise<any> => {
  const createBookingNotification = firebase.functions.httpsCallable('sendNewBookingEmail')

  try {
    return await createBookingNotification(bookingData)
  } catch (error) {
    console.log(error)

    throw new Error('Hubo un error mientras se intentaba mandar el email de creación de la cita')
  }
}

export const sendEditBookingNotification = async (bookingData: Record<string, any>): Promise<any> => {
  const createEditBookingNotification = firebase.functions.httpsCallable('sendEditBookingEmail')

  try {
    return await createEditBookingNotification(bookingData)
  } catch (error) {
    console.log(error)

    throw new Error('Hubo un error mientras se intentaba mandar el email de modificación de la cita')
  }
}


export const notifyBookingResult = async (booking: Record<string, any>, type: 'approved' | 'rejected'): Promise<any> => {
  const fn = firebase.functions.httpsCallable('notifyBookingResult')

  try {
    return await fn({ booking, type })
  } catch (error) {
    console.log(error)
    throw new Error('Hubo un error mientras se intentaba avisar al colaborador del resultado de la cita')
  }
}

export const deleteBooking = async (uid: string): Promise<any> => {
  // La function borra la cita y libera la franja de forma atómica, y devuelve el error
  // de forma síncrona (el antiguo trigger onDelete no permitía avisar al usuario).
  const deleteBookingCloud = firebase.functions.httpsCallable('deleteBookingCloud')
  try {
    return await deleteBookingCloud({ bookingId: uid })
  } catch (error) {
    console.log(error)
    throw new Error('Hubo un error mientras se intentaba eliminar la cita')
  }
}

export const updateBooking = async (bookingData: Record<string, any>): Promise<any> => {
  try {
    return firebase.db.collection('bookings').doc(bookingData.id).update(bookingData)
  } catch (error) {
    throw new Error('Hubo un error mientras se intentaba actualizar los datos de la cita')
  }
}

// Edición que puede cambiar de franja: la function libera la franja antigua y reserva
// la nueva de forma atómica junto con la actualización de la cita. Usar solo cuando la
// cita tiene storeId y datos de fecha/hora (no para el simple toggle de 'completed').
export const moveBooking = async (bookingData: Record<string, any>): Promise<any> => {
  const updateBookingCloud = firebase.functions.httpsCallable('updateBookingCloud')
  try {
    return await updateBookingCloud(bookingData)
  } catch (error: any) {
    console.log(error)
    const msg = error?.message || ''
    if (msg.includes('SLOT_TAKEN')) throw new Error('SLOT_TAKEN')
    if (msg.includes('SLOT_NOT_FOUND')) throw new Error('SLOT_NOT_FOUND')
    throw new Error('Hubo un error mientras se intentaba actualizar los datos de la cita')
  }
}

// export const getStorePhysio = async (storeId: string): Promise<any> => {
//   try {
//     return await firebase.db
//       .collection('users')
//       .where('storeId', '==', storeId)
//       .where(`role.id`, '==', 'role2')
//       .get()
//       .then((snapshot: any) => {
//         if (snapshot.empty) {
//           return []
//         }
//         return snapshot.docs.map((doc: any) => {
//           return { id: doc.id, ...doc.data() }
//         })
//       })
//   } catch (error) {
//     throw new Error('Hubo un error mientras se intentaba recuperar el calendario del fisio')
//   }
// }

// export const updatePhysioCalendar = async (physioId: string, calendar: Record<string, any>): Promise<any> => {
//   try {
//     return await firebase.db.collection('users').doc(physioId).update({ calendar: calendar })
//   } catch (error) {
//     throw new Error('Hubo un error mientras se intentaba actualizar el calendario del fisioterapeuta')
//   }
// }

export const getAllIncidentsByCompany = async (companyId: string): Promise<any> => {
  try {
    return await firebase.db
      .collection('incidents')
      .where('companyId', '==', companyId)
      .get()
      .then((snapshot: any) => {
        if (snapshot.empty) {
          return []
        }
        return snapshot.docs.map((doc: any) => {
          return { id: doc.id, ...doc.data() }
        })
      })
  } catch (error) {
    throw new Error('Hubo un error mientras se intentaban recuperar los incidentes')
  }
}

export const getAllCampaignsByCompany = async (companyId: string): Promise<any> => {
  try {
    return await firebase.db
      .collection('campaigns')
      .where('companyId', '==', companyId)
      .get()
      .then((snapshot: any) => {
        if (snapshot.empty) {
          return []
        }
        return snapshot.docs.map((doc: any) => {
          return { id: doc.id, ...doc.data() }
        })
      })
  } catch (error) {
    throw new Error('Hubo un error mientras se intentaban recuperar las campañas')
  }
}

export const exportBookingsForMetaleaves = async (filters: { year?: number; month?: number }): Promise<any> => {
  const exportBookings = firebase.functions.httpsCallable('exportBookingsForMetaleaves')

  try {
    const result = await exportBookings(filters)
    return result.data
  } catch (error: any) {
    console.log(error)
    throw new Error('Hubo un error mientras se intentaba exportar los bookings')
  }
}

export const sendMetaleavesReport = async (reportData: {
  recipientEmail: string
  startDate: string
  endDate: string
  storeFilter: string
  sectorFilter: string
  kpis: any
  problemAreas: any[]
}): Promise<any> => {
  const sendReport = firebase.functions.httpsCallable('sendMetaleavesReport')

  try {
    const result = await sendReport(reportData)
    return result.data
  } catch (error: any) {
    console.log(error)
    throw new Error(error.message || 'Hubo un error mientras se intentaba enviar el email')
  }
}

